<template>
  <BittsLoading :is-loading="true" />
</template>

<script>
import { BittsLoading } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import axios from 'axios';
import { get } from 'lodash';
import { mapActions } from 'pinia';

import useAuth from '@/composables/useAuth';
import useHasSmartOnboardingAccess from '@/composables/useHasSmartOnboardingAccess';
import useOfflinePartner from '@/composables/useOfflinePartner';
import {
  DATA_TEMPLATES_CRMS,
  MD_DATA_SOURCE_TYPE,
  REQUIRED_FEED_STATUS,
} from '@/constants/data_sources';
import { TEMP_OFFLINE_PARTNER_GOOGLE_SHEETS } from '@/constants/feature_flags';
import { useFeatureFlagStore, useFeedsStore, useFlashesStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';
import urls from '@/urls';
import { takeOfflineSheetSetup } from '@/utils/offlineSheetSetup';

export default {
  components: { BittsLoading },
  setup() {
    useHead({
      title: 'OAuth - Crossbeam',
    });
    const { loadUserProfile } = useRootStore();
    const { currentOrg } = useAuth();
    const isSmartOnboarding = useHasSmartOnboardingAccess();
    const { refreshOfflineData } = useOfflinePartner();
    const { hasFeatureFlag } = useFeatureFlagStore();
    return {
      loadUserProfile,
      currentOrg,
      isSmartOnboarding,
      refreshOfflineData,
      hasFeatureFlag,
    };
  },
  async created() {
    let nextPage = null;
    const params = this.$route.query;

    const stashed = takeOfflineSheetSetup();
    /* Google echoes the state token back on success and on failure alike, so it
       tells us whether this callback belongs to the stashed setup */
    const offlineSheetSetup =
      stashed && stashed.state && stashed.state === params.state
        ? stashed
        : null;

    const prefix = !this.currentOrg.setup_menu_hidden
      ? '/onboarding'
      : '/data-sources';

    let response;

    try {
      response = await axios.get(urls.oauth.callback, { params });
    } catch (err) {
      /* Salesforce external client apps must be installed in the connecting
         org before it can authorize them. Muncher answers with the install
         URL; send the admin there rather than to a dead-end error. */
      const installUrl = get(err, ['response', 'data', 'install_url']);
      if (installUrl && installUrl.startsWith('https://')) {
        window.location.href = installUrl;
        return;
      }
      const errMsg = get(err, ['response', 'data', 'error']);
      const toastObj = {
        message: 'Data Source Connection Failed',
        description: errMsg,
      };
      if (errMsg) this.addErrorFlash(toastObj);
      this.$router.push(
        offlineSheetSetup
          ? this.offlinePartnerDestination(offlineSheetSetup)
          : { path: prefix },
      );
      return;
    }

    /* Check if we're on the first oAuth of Microsoft Dynamics,
        and if so, then just send the user to the pick-CRM component
        which will make another oAuth for feed creation */
    const isMdDiscovery =
      response.data.integration_type === MD_DATA_SOURCE_TYPE;
    if (isMdDiscovery) {
      const instances = response.data.instances.value;
      this.$router.push({
        path: `${prefix}/dynamics-pick-crm`,
        query: {
          instances: JSON.stringify(instances),
        },
      });
      return;
    }

    if (offlineSheetSetup) {
      /* The setup route 404s once the flag is off, so fall back to the partner */
      const canResumeSetup = this.hasFeatureFlag(
        TEMP_OFFLINE_PARTNER_GOOGLE_SHEETS,
      );
      try {
        await this.refreshOfflineData(offlineSheetSetup.offlinePartnerUuid);
        await this.loadUserProfile();
      } finally {
        this.$router.push(
          canResumeSetup
            ? {
                name: 'offline_partner_google_sheets_setup',
                params: { partner_org_id: offlineSheetSetup.offlinePartnerId },
                query: {
                  tab: 'data',
                  offlinePartnerId: offlineSheetSetup.offlinePartnerId,
                  offlinePartnerUuid: offlineSheetSetup.offlinePartnerUuid,
                },
              }
            : this.offlinePartnerDestination(offlineSheetSetup),
        );
      }
      return;
    }

    try {
      // Hubspot requires a redirect_uri to the /oauth/callback page,
      // so we track our starting point in oauth state on the backend
      if (this.isSmartOnboarding && prefix === '/onboarding') {
        nextPage = '/onboarding';
        await this.refreshFeedsStore();
      } else {
        nextPage = `${prefix}/feed/${response.data.feed.id}?successfulOauth=true`;
        if (response.data.feed.status === REQUIRED_FEED_STATUS) {
          const toDataTemplates = DATA_TEMPLATES_CRMS.includes(
            response.data.feed.integration?.type,
          );
          nextPage = toDataTemplates
            ? `${prefix}/feed/${response.data.feed.id}/setup`
            : `${prefix}/feed/${response.data.feed.id}/settings`;
          await this.refreshFeedsStore();
        }
      }
      await this.loadUserProfile();
    } catch (err) {
      const errMsg = get(err, ['response', 'data', 'error']);
      const toastObj = {
        message: 'Data Source Connection Failed',
        description: errMsg,
      };
      if (errMsg) {
        this.addErrorFlash(toastObj);
      } else {
        throw err;
      }
    } finally {
      this.$router.push(nextPage || prefix);
    }
  },
  methods: {
    ...mapActions(useFlashesStore, ['addErrorFlash']),
    ...mapActions(useFeedsStore, ['refreshFeedsStore']),
    offlinePartnerDestination({ offlinePartnerId }) {
      return {
        name: 'partner_details',
        params: { partner_org_id: offlinePartnerId },
        query: { tab: 'data' },
      };
    },
  },
};
</script>
